/**
 * Best-effort IANA timezone from venue address (no external APIs).
 * US: prefers 2-letter state in "county"; else ZIP3 heuristics; else Eastern default.
 */

const US_STATE_TO_TZ = {
  AK: 'America/Anchorage',
  AL: 'America/Chicago',
  AR: 'America/Chicago',
  AZ: 'America/Phoenix',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DC: 'America/New_York',
  DE: 'America/New_York',
  FL: 'America/New_York',
  GA: 'America/New_York',
  HI: 'Pacific/Honolulu',
  IA: 'America/Chicago',
  ID: 'America/Boise',
  IL: 'America/Chicago',
  IN: 'America/Indiana/Indianapolis',
  KS: 'America/Chicago',
  KY: 'America/New_York',
  LA: 'America/Chicago',
  MA: 'America/New_York',
  MD: 'America/New_York',
  ME: 'America/New_York',
  MI: 'America/Detroit',
  MN: 'America/Chicago',
  MO: 'America/Chicago',
  MS: 'America/Chicago',
  MT: 'America/Denver',
  NC: 'America/New_York',
  ND: 'America/Chicago',
  NE: 'America/Chicago',
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NV: 'America/Los_Angeles',
  NY: 'America/New_York',
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles',
  PA: 'America/New_York',
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago',
  TN: 'America/Chicago',
  TX: 'America/Chicago',
  UT: 'America/Denver',
  VA: 'America/New_York',
  VT: 'America/New_York',
  WA: 'America/Los_Angeles',
  WI: 'America/Chicago',
  WV: 'America/New_York',
  WY: 'America/Denver',
};

/** Map common full state names (trimmed, case-insensitive) → code */
const US_STATE_NAME_TO_CODE = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

const COUNTRY_DEFAULT_TZ = {
  IE: 'Europe/Dublin',
  GB: 'Europe/London',
  FR: 'Europe/Paris',
  ES: 'Europe/Madrid',
  IT: 'Europe/Rome',
  DE: 'Europe/Berlin',
  NL: 'Europe/Amsterdam',
  BE: 'Europe/Brussels',
  PT: 'Europe/Lisbon',
  AT: 'Europe/Vienna',
  CH: 'Europe/Zurich',
  PL: 'Europe/Warsaw',
  SE: 'Europe/Stockholm',
  NO: 'Europe/Oslo',
  DK: 'Europe/Copenhagen',
  FI: 'Europe/Helsinki',
  NZ: 'Pacific/Auckland',
  JP: 'Asia/Tokyo',
  SG: 'Asia/Singapore',
};

const CA_PROVINCE_TO_TZ = {
  AB: 'America/Edmonton',
  BC: 'America/Vancouver',
  MB: 'America/Winnipeg',
  NB: 'America/Moncton',
  NL: 'America/St_Johns',
  NS: 'America/Halifax',
  NT: 'America/Yellowknife',
  NU: 'America/Iqaluit',
  ON: 'America/Toronto',
  PE: 'America/Halifax',
  QC: 'America/Toronto',
  SK: 'America/Regina',
  YT: 'America/Whitehorse',
};

const AU_STATE_TO_TZ = {
  NSW: 'Australia/Sydney',
  VIC: 'Australia/Melbourne',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  WA: 'Australia/Perth',
  TAS: 'Australia/Hobart',
  NT: 'Australia/Darwin',
  ACT: 'Australia/Sydney',
};

function normalizeUsState(raw) {
  if (raw == null) return null;
  const t = String(raw).trim();
  if (!t) return null;
  const upper = t.toUpperCase();
  if (upper.length === 2 && US_STATE_TO_TZ[upper]) return upper;
  const key = t.toLowerCase();
  return US_STATE_NAME_TO_CODE[key] || null;
}

function normalizeCaProvince(raw) {
  if (raw == null) return null;
  const upper = String(raw).trim().toUpperCase();
  if (upper.length === 2 && CA_PROVINCE_TO_TZ[upper]) return upper;
  return null;
}

function normalizeAuState(raw) {
  if (raw == null) return null;
  const upper = String(raw).trim().toUpperCase();
  if (AU_STATE_TO_TZ[upper]) return upper;
  return null;
}

/** US ZIP5 → rough IANA when state unknown (many edge cases; good enough as hint). */
function guessUsFromZip(zipDigits) {
  const z = zipDigits.replace(/\D/g, '').slice(0, 5);
  if (z.length < 3) return null;
  const n3 = parseInt(z.slice(0, 3), 10);
  if (Number.isNaN(n3)) return null;
  if (z.startsWith('967') || z.startsWith('968')) return 'Pacific/Honolulu';
  if (z.startsWith('995') || z.startsWith('996') || z.startsWith('997') || z.startsWith('998') || z.startsWith('999')) {
    return 'America/Anchorage';
  }
  // Contiguous US by ZIP3 bands (simplified)
  if (n3 >= 350 && n3 <= 369) return 'America/Chicago';
  if (n3 >= 590 && n3 <= 599) return 'America/Denver';
  if (n3 >= 600 && n3 <= 679) return 'America/Chicago';
  if (n3 >= 680 && n3 <= 693) return 'America/Chicago';
  if (n3 >= 700 && n3 <= 799) return 'America/Chicago';
  if (n3 >= 800 && n3 <= 816) return 'America/Denver';
  if (n3 >= 820 && n3 <= 838) return 'America/Denver';
  if (n3 >= 840 && n3 <= 847) return 'America/Denver';
  if (n3 >= 850 && n3 <= 865) return 'America/Phoenix';
  if (n3 >= 870 && n3 <= 884) return 'America/Denver';
  if (n3 >= 889 && n3 <= 899) return 'America/Los_Angeles';
  if (n3 >= 900 && n3 <= 961) return 'America/Los_Angeles';
  if (n3 >= 970 && n3 <= 979) return 'America/Los_Angeles';
  if (n3 >= 980 && n3 <= 994) return 'America/Los_Angeles';
  // 010–349: mostly Eastern / Central — bias Eastern for 0–2, Central for 3–5
  if (n3 <= 299) return 'America/New_York';
  if (n3 <= 499) return 'America/Chicago';
  return 'America/New_York';
}

/**
 * @param {Record<string, string|undefined>} address — expects country (ISO2), county (state), zip, etc.
 * @returns {string|null} IANA timezone id or null if not enough to guess
 */
export function guessTimezoneFromAddress(address) {
  if (!address || typeof address !== 'object') return null;
  const country = String(address.country || '').trim().toUpperCase();
  if (!country) return null;

  const county = address.county;
  const zipRaw = String(address.zip || '').trim();

  if (country === 'US') {
    const st = normalizeUsState(county);
    if (st && US_STATE_TO_TZ[st]) return US_STATE_TO_TZ[st];
    const zipGuess = guessUsFromZip(zipRaw);
    if (zipGuess) return zipGuess;
    return 'America/New_York';
  }

  if (country === 'CA') {
    const prov = normalizeCaProvince(county);
    if (prov && CA_PROVINCE_TO_TZ[prov]) return CA_PROVINCE_TO_TZ[prov];
    return 'America/Toronto';
  }

  if (country === 'AU') {
    const au = normalizeAuState(county);
    if (au && AU_STATE_TO_TZ[au]) return AU_STATE_TO_TZ[au];
    return 'Australia/Sydney';
  }

  return COUNTRY_DEFAULT_TZ[country] || null;
}
